/**
 * TeamPermissionsPanel - Iter 113c.
 * Tableau visuel de qui voit quoi : roles, branches, cross-branch.
 * Permet aux Admin/CEO de modifier la branche + cross-branch sans passer par l'UI users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  ceo: 'CEO', admin: 'Admin', julie: 'Project Manager',
  carla: 'CRM', amelie: 'Admin', collaborateur: 'Collaborateur',
  client: 'Client', courtier: 'Courtier', notaire: 'Notaire',
};

const ROLE_COLOR = {
  ceo: 'red', admin: 'purple', julie: 'emerald', carla: 'amber',
  amelie: 'purple', collaborateur: 'gray', courtier: 'blue', notaire: 'indigo',
};

const Badge = ({ children, color = 'gray' }) => (
  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded bg-${color}-100 text-${color}-700 font-semibold`}>
    {children}
  </span>
);

const TeamPermissionsPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/users`);
      const internal = (r.data || []).filter((u) =>
        ['ceo', 'admin', 'julie', 'carla', 'amelie', 'collaborateur', 'courtier'].includes(u.role)
      );
      setUsers(internal);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (user, patch) => {
    setSavingId(user.user_id);
    try {
      await axios.put(`${API}/admin/users/${user.user_id}`, patch);
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? { ...u, ...patch } : u)));
      toast.success(`${user.name} mis a jour`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSavingId(null); }
  };

  // Iter 113i : selecteur 3-options combinant branch + cross_branch_access
  // "bretagne" -> {branch: bretagne, cross: false}
  // "nord"     -> {branch: nord, cross: false}
  // "both"     -> {branch: bretagne, cross: true} (bretagne + nord)
  const scopeOf = (u) => (u.cross_branch_access ? 'both' : (u.branch || 'bretagne'));
  const updateScope = (u, scope) => {
    if (scope === 'both') return update(u, { branch: 'bretagne', cross_branch_access: true });
    return update(u, { branch: scope, cross_branch_access: false });
  };

  const filtered = users.filter((u) => {
    if (filter === 'all') return true;
    if (filter === 'both') return !!u.cross_branch_access;
    if (filter === 'bretagne') return !u.cross_branch_access && u.branch === 'bretagne';
    if (filter === 'nord') return !u.cross_branch_access && u.branch === 'nord';
    return true;
  });

  const counts = {
    total: users.length,
    bretagne: users.filter((u) => !u.cross_branch_access && u.branch === 'bretagne').length,
    nord: users.filter((u) => !u.cross_branch_access && u.branch === 'nord').length,
    both: users.filter((u) => u.cross_branch_access).length,
  };

  return (
    <div className="realease-card p-6" data-testid="team-permissions-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Equipe &amp; Permissions
            <Badge color="amber">ITER 113</Badge>
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Visualisation des branches et de l&apos;acces cross-branche. Ajustez la branche ou activez le cross-branche en un clic.
          </p>
        </div>
        <div className="flex gap-2 text-[11px]">
          <button type="button" onClick={() => setFilter('all')}
            className={`px-2 py-1 rounded ${filter === 'all' ? 'bg-[#13736D] text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid="team-filter-all">Tous ({counts.total})</button>
          <button type="button" onClick={() => setFilter('bretagne')}
            className={`px-2 py-1 rounded ${filter === 'bretagne' ? 'bg-[#13736D] text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid="team-filter-bretagne">Bretagne ({counts.bretagne})</button>
          <button type="button" onClick={() => setFilter('nord')}
            className={`px-2 py-1 rounded ${filter === 'nord' ? 'bg-[#13736D] text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid="team-filter-nord">Hauts-de-France ({counts.nord})</button>
          <button type="button" onClick={() => setFilter('both')}
            className={`px-2 py-1 rounded ${filter === 'both' ? 'bg-[#A57F4F] text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid="team-filter-both">Les deux ({counts.both})</button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500 p-4">Chargement...</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Membre</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Role</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Perimetre</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Voit</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Récap gestion locative</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const scope = scopeOf(u);
                const sees = scope === 'both' ? 'Bretagne + Hauts-de-France' :
                  (scope === 'nord' ? 'Hauts-de-France uniquement' : 'Bretagne uniquement');
                const saving = savingId === u.user_id;
                return (
                  <tr key={u.user_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{u.name}</div>
                      <div className="text-[11px] text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge color={ROLE_COLOR[u.role] || 'gray'}>{ROLE_LABEL[u.role] || u.role}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={scope}
                        onChange={(e) => updateScope(u, e.target.value)}
                        disabled={saving}
                        className={`text-xs border rounded px-2 py-1 bg-white font-semibold ${scope === 'both' ? 'border-[#A57F4F] text-[#A57F4F]' : (scope === 'nord' ? 'border-blue-300 text-blue-700' : 'border-emerald-300 text-emerald-700')}`}
                        data-testid={`team-scope-${u.user_id}`}
                      >
                        <option value="bretagne">Bretagne uniquement</option>
                        <option value="nord">Hauts-de-France uniquement</option>
                        <option value="both">Bretagne + Hauts-de-France</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{sees}</td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!u.gl_recap_recipient}
                        onChange={(e) => update(u, { gl_recap_recipient: e.target.checked })}
                        disabled={saving}
                        className="w-4 h-4 accent-[#13736D] cursor-pointer"
                        title={`Reçoit le récap mensuel de gestion locative (${u.branch === 'nord' ? 'Hauts-de-France' : 'Bretagne'}) le 15 du mois`}
                        data-testid={`team-recap-${u.user_id}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Aucun membre dans ce filtre.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-3">
        💡 Choisissez le perimetre d&apos;acces de chaque membre en un clic : Bretagne, Hauts-de-France, ou les deux.
        Le mode &laquo;&nbsp;Bretagne + Hauts-de-France&nbsp;&raquo; est reserve aux profils globaux (CEO, PM, Admin, CRM lead).
      </p>
    </div>
  );
};

export default TeamPermissionsPanel;
